import { useEffect, useRef, useState } from "react";
import { useNoctua } from "./NoctuaContext";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Database, Upload, MapPin, FileSpreadsheet, CheckCircle2 } from "lucide-react";

export default function DatosPage() {
  const { selectedState: ctxState, setSelectedState: setCtxState, allStates: ctxStates } = useNoctua();
  const [allStates, setAllStates] = useState([]);
  const [counts, setCounts] = useState({ total: 0, with_geometry: 0 });
  const [statesSeeded, setStatesSeeded] = useState({});
  const selectedState = String(ctxState);
  const setSelectedState = (v) => setCtxState(v);
  const geoRef = useRef(null);
  const csvRef = useRef(null);
  const secRef = useRef(null);

  const [geoNameProp, setGeoNameProp] = useState("NOMGEO");
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoResult, setGeoResult] = useState(null);

  const [csvAnio, setCsvAnio] = useState("2024");
  const [csvNameCol, setCsvNameCol] = useState("MUNICIPIO");
  const [csvListaCol, setCsvListaCol] = useState("LISTA_NOMINAL");
  const [csvPartCol, setCsvPartCol] = useState("PARTICIPACION");
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const [secAnio, setSecAnio] = useState("2024");
  const [secLoading, setSecLoading] = useState(false);
  const [secResult, setSecResult] = useState(null);
  const [secStats, setSecStats] = useState(null);

  const [evalLoading, setEvalLoading] = useState(false);
  const [evalResult, setEvalResult] = useState(null);

  const refresh = async () => {
    const r = await api.get(`/noctua/municipios?estado_clave=${selectedState}&limit=2000`);
    setCounts({
      total: r.data.length,
      with_geometry: r.data.filter((m) => m.has_geometry).length,
    });
    const stats = await api.get(`/noctua/secciones/stats?estado_clave=${selectedState}`).catch(() => ({ data: null }));
    setSecStats(stats.data);
  };

  useEffect(() => {
    if (ctxStates && ctxStates.length) setAllStates(ctxStates);
    else api.get("/catalog/states").then((r) => setAllStates(r.data));
  }, [ctxStates]);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [selectedState]);

  const seedState = async (clave) => {
    setStatesSeeded((s) => ({ ...s, [clave]: "loading" }));
    try {
      const r = await api.post(`/noctua/municipios/seed-state/${clave}`);
      // Always rescatter after seeding so municipios spread out on the map
      await api.post(`/noctua/municipios/rescatter/${clave}`).catch(() => {});
      setStatesSeeded((s) => ({ ...s, [clave]: r.data }));
      toast.success(`${r.data.count} municipios sembrados${r.data.skipped ? " (ya existían)" : ""}`);
      if (String(clave) === selectedState) refresh();
    } catch (err) {
      setStatesSeeded((s) => ({ ...s, [clave]: "error" }));
      toast.error("Error sembrando");
    }
  };

  const upload = async (kind) => {
    const refs = { geo: geoRef, csv: csvRef, sec: secRef };
    const file = refs[kind].current?.files?.[0];
    if (!file) { toast.error("Selecciona un archivo"); return; }
    const setLoading = { geo: setGeoLoading, csv: setCsvLoading, sec: setSecLoading }[kind];
    const setResult = { geo: setGeoResult, csv: setCsvResult, sec: setSecResult }[kind];
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("estado_clave", selectedState);
      if (kind === "geo") {
        fd.append("name_property", geoNameProp);
      } else if (kind === "csv") {
        fd.append("anio", csvAnio);
        fd.append("name_column", csvNameCol);
        fd.append("lista_nominal_column", csvListaCol);
        fd.append("participacion_column", csvPartCol);
      } else if (kind === "sec") {
        fd.append("anio", secAnio);
      }
      const url = kind === "geo" ? "/noctua/municipios/upload-geojson" :
                  kind === "csv" ? "/noctua/resultados/upload-csv" :
                  "/noctua/secciones/upload-csv";
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.imported ?? r.data.matched ?? 0} elementos procesados`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const evaluateModel = async () => {
    setEvalLoading(true);
    try {
      const r = await api.post(`/noctua/predict/evaluate/${selectedState}`);
      setEvalResult(r.data);
      toast.success(`Modelo evaluado contra ${r.data.n_municipios_evaluados} municipios`);
    } catch (err) {
      toast.error("Error evaluando modelo");
    } finally {
      setEvalLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="noctua-datos-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Gestión de datos</div>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-white">Datos</h1>
        <p className="text-sm text-slate-400 mt-1">
          32 estados · resultados municipales y seccionales · evaluación del modelo ML
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Estado activo</Label>
          <Select value={selectedState} onValueChange={setSelectedState}>
            <SelectTrigger className="w-64 bg-slate-900 border-slate-700 text-white mt-1" data-testid="datos-state-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 max-h-80">
              {allStates.map((s) => (
                <SelectItem key={s.clave} value={String(s.clave)} className="text-white">{String(s.clave).padStart(2, "0")} · {s.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Stat label="Municipios" value={counts.total} />
        <Stat label="Con geometría" value={counts.with_geometry} />
        <Stat label="Secciones" value={secStats?.total ?? 0} />
        <div className="ml-auto">
          <Button onClick={() => seedState(selectedState)} className="bg-blue-600 hover:bg-blue-700" data-testid="seed-current-state-button">
            <MapPin className="w-4 h-4 mr-2" /> Sembrar estado actual
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* GeoJSON */}
        <div className="noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-400" /> 1. GeoJSON municipal (INEGI MGN)</h3>
          <p className="text-xs text-slate-400 mt-1">Descarga el MGN del INEGI por estado. Propiedad por defecto: <code className="text-blue-300">NOMGEO</code>.</p>
          <div className="mt-4 space-y-3">
            <Field label="Propiedad de nombre">
              <Input value={geoNameProp} onChange={(e) => setGeoNameProp(e.target.value)} className="bg-slate-900 border-slate-700 text-white" data-testid="geo-name-prop" />
            </Field>
            <Field label="Archivo GeoJSON">
              <input ref={geoRef} type="file" accept=".geojson,.json,application/geo+json,application/json" className="block w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700" data-testid="geo-file-input" />
            </Field>
            <Button onClick={() => upload("geo")} disabled={geoLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="geo-upload-button">
              <Upload className="w-4 h-4 mr-2" /> {geoLoading ? "Procesando…" : "Subir GeoJSON"}
            </Button>
            {geoResult && (
              <div className="mt-3 text-xs text-slate-300 p-3 bg-slate-900/60 rounded">
                <div className="flex items-center gap-2 text-green-400"><CheckCircle2 className="w-3.5 h-3.5" /> {geoResult.matched} municipios actualizados</div>
                {geoResult.unmatched_total > 0 && <div className="mt-1 text-yellow-300">⚠ {geoResult.unmatched_total} sin match</div>}
              </div>
            )}
          </div>
        </div>

        {/* Municipal CSV */}
        <div className="noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-blue-400" /> 2. CSV resultados municipales</h3>
          <p className="text-xs text-slate-400 mt-1">
            <a href="https://sicee.ine.mx/downloadzip" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">sicee.ine.mx/downloadzip</a>
            <br />Columnas de partido: MORENA, PAN, PRI, MC, PVEM, PRD, PT, NA, OTROS.
          </p>
          <div className="mt-4 space-y-3">
            <Field label="Año">
              <Select value={csvAnio} onValueChange={setCsvAnio}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="csv-year"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {[2018, 2021, 2024].map((y) => <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Col. mun."><Input value={csvNameCol} onChange={(e) => setCsvNameCol(e.target.value)} className="bg-slate-900 border-slate-700 text-white text-xs" data-testid="csv-name-col" /></Field>
              <Field label="Col. lista nom."><Input value={csvListaCol} onChange={(e) => setCsvListaCol(e.target.value)} className="bg-slate-900 border-slate-700 text-white text-xs" data-testid="csv-lista-col" /></Field>
              <Field label="Col. partic."><Input value={csvPartCol} onChange={(e) => setCsvPartCol(e.target.value)} className="bg-slate-900 border-slate-700 text-white text-xs" data-testid="csv-part-col" /></Field>
            </div>
            <Field label="Archivo CSV">
              <input ref={csvRef} type="file" accept=".csv,text/csv" className="block w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700" data-testid="csv-file-input" />
            </Field>
            <Button onClick={() => upload("csv")} disabled={csvLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="csv-upload-button">
              <Upload className="w-4 h-4 mr-2" /> {csvLoading ? "Importando…" : "Importar CSV"}
            </Button>
            {csvResult && (
              <div className="mt-3 text-xs text-slate-300 p-3 bg-slate-900/60 rounded">
                <div className="flex items-center gap-2 text-green-400"><CheckCircle2 className="w-3.5 h-3.5" /> {csvResult.imported} filas</div>
                {csvResult.skipped_total > 0 && <div className="mt-1 text-yellow-300">⚠ {csvResult.skipped_total} omitidas</div>}
              </div>
            )}
          </div>
        </div>

        {/* Section CSV */}
        <div className="noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-blue-400" /> 3. CSV resultados seccionales</h3>
          <p className="text-xs text-slate-400 mt-1">Una fila por sección electoral. Columnas: <code className="text-blue-300">SECCION</code>, <code className="text-blue-300">MUNICIPIO</code> (opc.), LISTA_NOMINAL, PARTICIPACION + columnas por partido.</p>
          <div className="mt-4 space-y-3">
            <Field label="Año">
              <Select value={secAnio} onValueChange={setSecAnio}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="sec-year"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {[2018, 2021, 2024].map((y) => <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Archivo CSV (seccional)">
              <input ref={secRef} type="file" accept=".csv,text/csv" className="block w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700" data-testid="sec-file-input" />
            </Field>
            <Button onClick={() => upload("sec")} disabled={secLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="sec-upload-button">
              <Upload className="w-4 h-4 mr-2" /> {secLoading ? "Importando…" : "Importar CSV seccional"}
            </Button>
            {secResult && (
              <div className="mt-3 text-xs text-slate-300 p-3 bg-slate-900/60 rounded">
                <div className="flex items-center gap-2 text-green-400"><CheckCircle2 className="w-3.5 h-3.5" /> {secResult.imported} secciones</div>
                {secResult.skipped_total > 0 && <div className="mt-1 text-yellow-300">⚠ {secResult.skipped_total} omitidas</div>}
              </div>
            )}
            {secStats && secStats.total > 0 && (
              <div className="text-xs text-slate-400">
                {secStats.total.toLocaleString()} secciones cargadas en años: {secStats.anios.join(", ")}
              </div>
            )}
          </div>
        </div>

        {/* ML Evaluation */}
        <div className="noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white flex items-center gap-2"><Database className="w-4 h-4 text-blue-400" /> 4. Evaluación del modelo ML</h3>
          <p className="text-xs text-slate-400 mt-1">
            Cross-validation <em>leave-one-out</em>: entrena con ciclos anteriores y predice el más reciente.
            Requiere ≥3 ciclos por municipio.
          </p>
          <div className="mt-4 space-y-3">
            <Button onClick={evaluateModel} disabled={evalLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="evaluate-model-button">
              {evalLoading ? "Evaluando…" : "Evaluar modelo"}
            </Button>
            {evalResult && (
              <div className="mt-3 text-xs text-slate-300 p-3 bg-slate-900/60 rounded space-y-2">
                <div className="text-slate-400">
                  Evaluado en <strong className="text-white">{evalResult.n_municipios_evaluados}</strong> municipios
                </div>
                {evalResult.mae_por_partido_pts && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                      Error absoluto medio (MAE) por partido — menor es mejor
                    </div>
                    {Object.entries(evalResult.mae_por_partido_pts).filter(([, v]) => v !== null).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 my-1" data-testid={`mae-${k}`}>
                        <span className="w-12 text-slate-300">{k}</span>
                        <div className="flex-1 h-1.5 bg-slate-800 rounded overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-green-500 to-red-500" style={{ width: `${Math.min(v * 10, 100)}%` }} />
                        </div>
                        <span className="text-white tabular-nums w-14 text-right">{v} pts</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <DistritosUploader selectedState={selectedState} allStates={allStates} />

      {/* Bulk seed */}
      <div className="noctua-card p-5">
        <h3 className="font-display font-bold text-lg text-white">6. Sembrar 32 estados</h3>
        <p className="text-xs text-slate-400 mt-1">Inicializa municipios desde el EDMSLM (sin geometría real hasta subir GeoJSON).</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {allStates.map((s) => {
            const status = statesSeeded[s.clave];
            return (
              <button
                key={s.clave}
                onClick={() => seedState(s.clave)}
                disabled={status === "loading"}
                className="text-left bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded p-2 text-xs disabled:opacity-50 transition-colors"
                data-testid={`seed-state-${s.clave}`}
              >
                <div className="text-white">{String(s.clave).padStart(2, "0")} · {s.nombre}</div>
                {status === "loading" ? (
                  <div className="text-[10px] text-blue-400 mt-0.5">Sembrando…</div>
                ) : status && typeof status === "object" ? (
                  <div className="text-[10px] text-green-400 mt-0.5">✓ {status.count}{status.skipped ? " (ya existían)" : ""}</div>
                ) : (
                  <div className="text-[10px] text-slate-500 mt-0.5">Pendiente</div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="noctua-card p-3 min-w-[120px]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div className="mt-1 font-display font-black text-xl text-white tabular-nums">{(value || 0).toLocaleString()}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DistritosUploader({ selectedState, allStates }) {
  const fileRef = useRef(null);
  const [tipo, setTipo] = useState("federal");
  const [mode, setMode] = useState("csv"); // csv | shapefile
  const [distColumn, setDistColumn] = useState("DISTRITO");
  const [muniColumn, setMuniColumn] = useState("MUNICIPIO_CLAVE");
  const [secColumn, setSecColumn] = useState("SECCION");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [existing, setExisting] = useState([]);

  const refreshExisting = async () => {
    try {
      const r = await api.get(`/noctua/distritos?estado_clave=${selectedState}`);
      setExisting(r.data);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    refreshExisting();
  }, [selectedState]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("estado_clave", selectedState);
      fd.append("tipo", tipo);
      if (mode === "csv") {
        fd.append("distrito_column", distColumn);
        fd.append("municipio_column", muniColumn);
        fd.append("seccion_column", secColumn);
      } else {
        fd.append("distrito_property", distColumn);
        fd.append("municipio_property", muniColumn);
        fd.append("seccion_property", secColumn);
      }
      const endpoint = mode === "csv" ? "/noctua/distritos/upload-csv" : "/noctua/distritos/upload-shapefile";
      const r = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`${r.data.distritos_importados} distritos importados`);
      refreshExisting();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const deleteOne = async (d) => {
    if (!window.confirm(`Eliminar mapping del ${d.tipo} ${d.distrito_clave}?`)) return;
    try {
      await api.delete(`/noctua/distritos/${d.estado_clave}/${d.tipo}/${d.distrito_clave}`);
      toast.success("Distrito eliminado");
      refreshExisting();
    } catch (err) {
      toast.error("Error");
    }
  };

  const stateName = allStates.find((s) => String(s.clave) === String(selectedState))?.nombre;
  const isShp = mode === "shapefile";
  const acceptAttr = isShp ? ".zip,.kml,.kmz,application/zip,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz" : ".csv,text/csv";

  return (
    <div className="noctua-card p-5" data-testid="distritos-uploader">
      <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
        <MapPin className="w-4 h-4 text-amber-400" /> 5. Cartografía distrital (federal / local)
      </h3>
      <p className="text-xs text-slate-400 mt-1">
        Sube el mapping <strong className="text-white">distrito → municipios</strong> del INE / IEE para{" "}
        <span className="text-blue-300">{stateName}</span>. Cuando esté cargado, las subcuentas con scope distrital verán la vista filtrada automáticamente.
      </p>

      {/* Format toggle */}
      <div className="mt-3 inline-flex bg-slate-900 border border-slate-700 rounded-md overflow-hidden" data-testid="dist-mode-toggle">
        <button
          onClick={() => { setMode("csv"); setDistColumn("DISTRITO"); setMuniColumn("MUNICIPIO_CLAVE"); setSecColumn("SECCION"); }}
          className={`px-3 py-1.5 text-xs ${mode === "csv" ? "bg-amber-600 text-white" : "text-slate-300 hover:text-white"}`}
          data-testid="dist-mode-csv"
        >
          CSV
        </button>
        <button
          onClick={() => { setMode("shapefile"); setDistColumn("DISTRITO"); setMuniColumn("MUNICIPIO"); setSecColumn("SECCION"); }}
          className={`px-3 py-1.5 text-xs ${isShp ? "bg-amber-600 text-white" : "text-slate-300 hover:text-white"}`}
          data-testid="dist-mode-shapefile"
        >
          Shapefile (.zip) / KML / KMZ
        </button>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        {mode === "csv" ? (
          <>Formato mínimo: <code className="text-blue-300">DISTRITO,MUNICIPIO_CLAVE</code>. Opcional: columna <code className="text-blue-300">SECCION</code>.</>
        ) : (
          <>Sube el <strong>shapefile zippeado</strong> (.shp+.dbf+.shx dentro de un .zip) o <strong>KML/KMZ</strong> tal como los publica el INE ({" "}
            <a href="https://sicee.ine.mx/downloadzip" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">sicee.ine.mx</a>
            ) o el IEE local. El servidor extrae los atributos de cada polígono.</>
        )}
      </p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Tipo de distrito">
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="dist-tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="federal" className="text-white">Federal</SelectItem>
              <SelectItem value="local" className="text-white">Local</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={isShp ? "Archivo (.zip / .kml / .kmz)" : "Archivo CSV"}>
          <input
            ref={fileRef}
            type="file"
            accept={acceptAttr}
            className="block w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-amber-600 file:text-white hover:file:bg-amber-700"
            data-testid="dist-file"
          />
        </Field>
        <div className="grid grid-cols-3 gap-2 md:col-span-2">
          <Field label={isShp ? "Attr. distrito" : "Col. distrito"}>
            <Input value={distColumn} onChange={(e) => setDistColumn(e.target.value)} className="bg-slate-900 border-slate-700 text-white text-xs" data-testid="dist-col" />
          </Field>
          <Field label={isShp ? "Attr. municipio" : "Col. municipio"}>
            <Input value={muniColumn} onChange={(e) => setMuniColumn(e.target.value)} className="bg-slate-900 border-slate-700 text-white text-xs" data-testid="dist-muni-col" />
          </Field>
          <Field label={isShp ? "Attr. sección (opc.)" : "Col. sección (opc.)"}>
            <Input value={secColumn} onChange={(e) => setSecColumn(e.target.value)} className="bg-slate-900 border-slate-700 text-white text-xs" data-testid="dist-sec-col" />
          </Field>
        </div>
      </div>
      <Button onClick={upload} disabled={loading} className="mt-4 bg-amber-600 hover:bg-amber-700 w-full" data-testid="dist-upload">
        <Upload className="w-4 h-4 mr-2" /> {loading ? "Procesando…" : (isShp ? "Subir shapefile / KML" : "Subir CSV distrital")}
      </Button>
      {result && (
        <div className="mt-3 text-xs text-slate-300 p-3 bg-slate-900/60 rounded flex items-center gap-2 text-green-400" data-testid="dist-result">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {result.distritos_importados} distritos importados{result.features_processed ? ` · ${result.features_processed} features` : ""}{result.rows_processed ? ` · ${result.rows_processed} filas` : ""}{result.source ? ` · ${result.source.toUpperCase()}` : ""}
        </div>
      )}

      {existing.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">
            Distritos cargados en {stateName} · {existing.length} totales
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {existing.map((d) => (
              <div key={d.id} className="bg-slate-900 border border-slate-700 rounded p-2 text-xs" data-testid={`dist-item-${d.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">{d.tipo === "federal" ? "F" : "L"}·{d.distrito_clave}</span>
                  <button
                    onClick={() => deleteOne(d)}
                    className="text-red-400 hover:text-red-300 text-[10px]"
                    data-testid={`dist-delete-${d.id}`}
                  >
                    Eliminar
                  </button>
                </div>
                <div className="text-slate-400 text-[10px] mt-0.5">
                  {d.municipios_claves.length} municipios
                  {d.secciones?.length > 0 ? ` · ${d.secciones.length} secciones` : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
